import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../firebase/config';

const UserContext = createContext();

export const useUsers = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUsers must be used within UserProvider');
  }
  return context;
};

// Define user roles and their permissions
export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  KITCHEN: 'kitchen',
  DELIVERY: 'delivery',
  WAITER: 'waiter'
};

export const PERMISSIONS = {
  // Order permissions
  VIEW_ORDERS: ['admin', 'manager', 'kitchen', 'delivery', 'waiter'],
  EDIT_ORDERS: ['admin', 'manager', 'kitchen', 'waiter'],
  DELETE_ORDERS: ['admin', 'manager'],
  ASSIGN_DELIVERY: ['admin', 'manager', 'waiter'],
  CREATE_ORDERS: ['admin', 'manager', 'waiter'], // Waiters can take orders

  // Menu permissions
  VIEW_MENU: ['admin', 'manager', 'kitchen', 'waiter'],
  EDIT_MENU: ['admin', 'manager'],
  DELETE_MENU: ['admin'],

  // Table permissions
  VIEW_TABLES: ['admin', 'manager', 'waiter'],
  EDIT_TABLES: ['admin', 'manager', 'waiter'],
  MANAGE_TABLES: ['admin', 'manager', 'waiter'],

  // User permissions
  VIEW_USERS: ['admin', 'manager'],
  EDIT_USERS: ['admin'],
  DELETE_USERS: ['admin'],

  // Settings permissions
  VIEW_SETTINGS: ['admin', 'manager'],
  EDIT_SETTINGS: ['admin'],

  // Customer permissions
  VIEW_CUSTOMERS: ['admin', 'manager', 'waiter'],
  EDIT_CUSTOMERS: ['admin', 'manager'],

  // Analytics permissions
  VIEW_ANALYTICS: ['admin', 'manager'],

  // Reviews permissions
  VIEW_REVIEWS: ['admin', 'manager'],
  EDIT_REVIEWS: ['admin', 'manager'],

  // Bill permissions
  PRINT_BILLS: ['admin', 'manager', 'waiter'],
  VIEW_BILLS: ['admin', 'manager', 'waiter']
};

export const UserProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to users in real-time
  useEffect(() => {
    // Load all users (don't use orderBy to avoid excluding documents without createdAt field)
    const q = query(collection(db, 'users'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort in memory instead (users with createdAt first, then others)
      usersList.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toMillis() - a.createdAt.toMillis();
        }
        if (a.createdAt) return -1;
        if (b.createdAt) return 1;
        return 0;
      });

      console.log('👥 Loaded users:', usersList.length, 'users');
      setUsers(usersList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching users:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Add new user
  const addUser = async (userData) => {
    try {
      console.log('🔐 Creating user in Firebase Authentication...', userData.email);

      // Step 1: Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );
      const uid = userCredential.user.uid;

      console.log('✅ Firebase Auth user created with UID:', uid);

      // Step 2: Create Firestore document with matching UID
      const newUser = {
        email: userData.email,
        name: userData.name,
        phone: userData.phone,
        role: userData.role,
        isActive: userData.isActive !== undefined ? userData.isActive : true,
        assignedOutlets: userData.assignedOutlets || [],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
        // Note: Do NOT store password in Firestore (it's in Firebase Auth)
      };

      // Use setDoc with the UID as the document ID
      await setDoc(doc(db, 'users', uid), newUser);

      console.log('✅ Firestore document created with matching UID:', uid);
      console.log('📊 User data:', { role: newUser.role, isActive: newUser.isActive });

      return { success: true, id: uid };
    } catch (error) {
      console.error('❌ Error adding user:', error);
      let errorMessage = 'Failed to create user';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Email already in use';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak (minimum 6 characters)';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }

      return { success: false, error: errorMessage };
    }
  };

  // Update user
  const updateUser = async (userId, updates) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error };
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    try {
      await deleteDoc(doc(db, 'users', userId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error };
    }
  };

  // Toggle user active status
  const toggleUserStatus = async (userId, currentStatus) => {
    return updateUser(userId, { isActive: !currentStatus });
  };

  // Get users by role
  const getUsersByRole = (role) => {
    return users.filter(user => user.role === role);
  };

  // Get active delivery boys
  const getActiveDeliveryBoys = () => {
    return users.filter(user =>
      user.role === ROLES.DELIVERY && user.isActive === true
    );
  };

  // Check if user has permission
  const hasPermission = (userRole, permission) => {
    return PERMISSIONS[permission]?.includes(userRole) || false;
  };

  const value = {
    users,
    loading,
    addUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    getUsersByRole,
    getActiveDeliveryBoys,
    hasPermission,
    ROLES,
    PERMISSIONS
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
